import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowUpRight, ExternalLink, ChevronDown, Sparkles } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const CASE_STUDY_BODY = [
  "For El Cheapo Lifts (Cooper Equipment Rentals), we led a complete digital transformation focused on turning their website into a high-performing lead generation platform rather than just an online brochure.",
  "Our work included a full website redesign with improved navigation, stronger homepage messaging, and a modern layout designed to build trust and increase quote requests. We reorganized equipment categories to make browsing faster and more intuitive, implemented strategic calls-to-action throughout the site, and built a streamlined \u201cRequest a Quote\u201d system that captures higher-quality customer inquiries.",
  "From an SEO perspective, we developed the site\u2019s foundational search strategy, optimized content structure, and implemented on-page SEO best practices to support long-term organic growth. We also planned the website architecture with future expansion in mind, making it easy to add new equipment categories, service pages, and location-specific content as the business grows.",
  "Beyond design, the project focused heavily on communicating the company\u2019s competitive advantages\u2014including transparent pricing, fast delivery, contractor-friendly services, and reliable customer support\u2014to strengthen credibility and improve conversion rates.",
  "The result was a scalable, conversion-focused website built to support long-term digital marketing, SEO growth, and increased rental inquiries while providing a smooth transition from the previous website.",
];

const HIGHLIGHTS = [
  "Full website redesign · modern navigation · stronger messaging",
  "Reorganized equipment categories for faster browsing",
  "Streamlined \u201cRequest a Quote\u201d capture system",
  "Foundational SEO strategy + on-page optimization",
  "Architecture designed for future expansion",
];

const OurWorkPage = () => {
  const [showDetails, setShowDetails] = useState(true);

  return (
    <div className="min-h-screen bg-[#050914]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-36 pb-12 lg:pt-44 overflow-hidden" data-testid="our-work-hero">
        <div className="absolute inset-0 hero-mesh" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm mb-10 transition-colors" data-testid="our-work-back">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <p className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-white/50 font-bold mb-6">
            <span className="w-8 h-px bg-white/30" /> Featured Project · Rental
          </p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter text-white leading-[1.02]"
          >
            El Cheapo Lifts
            <span className="block text-white/40 text-3xl md:text-4xl lg:text-5xl mt-3">Cooper Equipment Rentals</span>
          </motion.h1>
          <p className="mt-7 text-white/60 text-lg lg:text-xl max-w-2xl font-light leading-relaxed">
            A complete digital transformation — website, messaging and SEO foundation designed to turn a static brochure into a lead generation platform for the rental business.
          </p>
        </div>
      </section>

      {/* 3 Action buttons */}
      <section className="relative pb-14 lg:pb-20" data-testid="our-work-actions">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-4">
            <button
              onClick={() => setShowDetails((v) => !v)}
              className="group relative flex items-center justify-between px-6 py-5 rounded-2xl border border-white/10 bg-[#0B1221] hover:border-white/25 transition-colors duration-300 text-left"
              data-testid="btn-what-we-did"
              aria-expanded={showDetails}
            >
              <div>
                <p className="text-[11px] uppercase tracking-[0.25em] text-white/45 font-bold mb-1">Case Study</p>
                <p className="font-display text-lg text-white leading-tight">What did we do for them</p>
              </div>
              <ChevronDown className={`w-5 h-5 text-white/60 transition-transform duration-300 ${showDetails ? 'rotate-180' : ''}`} />
            </button>

            <a
              href="https://elcheapolifts.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative flex items-center justify-between px-6 py-5 rounded-2xl border border-white/10 bg-[#0B1221] hover:border-white/25 transition-colors duration-300 text-left"
              data-testid="btn-old-website"
            >
              <div>
                <p className="text-[11px] uppercase tracking-[0.25em] text-white/45 font-bold mb-1">Before</p>
                <p className="font-display text-lg text-white leading-tight">Old website</p>
              </div>
              <ExternalLink className="w-5 h-5 text-white/60 group-hover:text-white transition-colors" />
            </a>

            <a
              href="https://testwebsiteweb.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative flex items-center justify-between px-6 py-5 rounded-2xl bg-[#FF6B35] hover:bg-[#ff7d4f] transition-colors duration-300 text-left"
              data-testid="btn-new-website"
            >
              <div>
                <p className="text-[11px] uppercase tracking-[0.25em] text-white/70 font-bold mb-1 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" /> After
                </p>
                <p className="font-display text-lg text-white leading-tight">Our new and best website</p>
              </div>
              <ArrowUpRight className="w-5 h-5 text-white group-hover:rotate-45 transition-transform duration-300" />
            </a>
          </div>
        </div>
      </section>

      {/* Case study body */}
      <AnimatePresence initial={false}>
        {showDetails && (
          <motion.section
            key="details"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative border-t border-white/5 overflow-hidden"
            data-testid="our-work-details"
          >
            <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-12 gap-10 lg:gap-14">
              <div className="lg:col-span-4">
                <p className="text-[11px] uppercase tracking-[0.25em] text-white/45 font-bold mb-4">What we did</p>
                <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-white leading-tight">
                  Rebuilt as a lead engine, not a brochure.
                </h2>
                <ul className="mt-8 space-y-3">
                  {HIGHLIGHTS.map((h) => (
                    <li key={h} className="flex items-start gap-3 text-sm text-white/70 font-light leading-relaxed">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#FF6B35] flex-shrink-0" />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:col-span-8 space-y-6">
                {CASE_STUDY_BODY.map((p, i) => (
                  <motion.p
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.08, duration: 0.5 }}
                    className="text-white/70 text-base lg:text-lg font-light leading-relaxed"
                  >
                    {p}
                  </motion.p>
                ))}
                <div className="pt-6 flex flex-wrap gap-3 border-t border-white/5">
                  <a
                    href="https://testwebsiteweb.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#050914] font-semibold hover:scale-[1.03] transition-transform"
                  >
                    View the new site
                    <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
                  </a>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
                  >
                    Book a similar project
                  </Link>
                </div>
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default OurWorkPage;
